import { FC } from 'react'
import { RouteComponentProps, Link } from 'react-router-dom'
import { useGetDraftReviewsQuery } from 'backoffice/components/Drafts/useDraftsQuery'
import { getDateTimeString } from 'lib/helpers/dateHelper'
import { Loading } from 'components/Loading'
import { ROUTES } from 'lib/constants'
import { Helmet } from 'react-helmet'
import { FiArrowLeft } from 'react-icons/fi'

import styles from './DraftReviewsPage.module.scss'

type DraftReviewsPageProps = RouteComponentProps<Record<string, string | undefined>>

const DraftReviewsPage: FC<DraftReviewsPageProps> = ({ match }) => {
  const draftId = parseInt(match.params.draftId ?? '', 10)
  const { data: draft, isLoading, isError } = useGetDraftReviewsQuery(draftId)

  if (isNaN(draftId)) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState}>
          <h2>Invalid Draft ID</h2>
          <Link to={ROUTES.dropRequests.path} className={styles.backLink}>
            <FiArrowLeft /> Back to Petitions
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <Helmet>
        <title>{`POAP - Draft #${draftId} Reviews`}</title>
      </Helmet>

      <div className={styles.header}>
        <Link to={ROUTES.dropRequests.path} className={styles.backLink}>
          <FiArrowLeft /> Back to Petitions
        </Link>
        <h1 className={styles.title}>Curation Comments — Draft #{draftId}</h1>
      </div>

      {isLoading && (
        <div className={styles.loadingContainer}>
          <Loading />
          <span>Loading reviews...</span>
        </div>
      )}

      {isError && (
        <div className={styles.errorState}>
          <h2>Failed to load reviews</h2>
          <p>Could not fetch reviews for draft #{draftId}. Please try again.</p>
          <Link to={ROUTES.dropRequests.path} className={styles.backLink}>
            <FiArrowLeft /> Back to Petitions
          </Link>
        </div>
      )}

      {draft && (
        <>
          {/* Draft summary card */}
          <div className={styles.draftSummary}>
            <div className={styles.draftImageContainer}>
              <img
                src={draft.data.cropImage?.url || draft.data.image?.url}
                alt={draft.data.title}
                className={styles.draftImage}
              />
            </div>
            <div className={styles.draftInfo}>
              <h2 className={styles.draftTitle}>{draft.data.title}</h2>
              <div className={styles.draftMeta}>
                <span className={styles.statusBadge} data-status={draft.status}>
                  {draft.status.replace('_', ' ')}
                </span>
                {draft.dropId && (
                  <span className={styles.metaItem}>
                    <strong>Drop:</strong> #{draft.dropId}
                  </span>
                )}
                {draft.issuerEmail && (
                  <span className={styles.metaItem}>
                    <strong>Issuer:</strong> {draft.issuerEmail}
                  </span>
                )}
                <span className={styles.metaItem}>
                  <strong>Created:</strong> {getDateTimeString(new Date(draft.createdAt))}
                </span>
                <span className={styles.metaItem}>
                  <strong>Supply:</strong> {draft.data.mintingConfiguration?.supply ?? 'N/A'}
                </span>
              </div>
              <p className={styles.draftDescription}>{draft.data.description}</p>
            </div>
          </div>

          {/* Reviews list */}
          <div className={styles.reviewsSection}>
            <div className={styles.reviewsHeader}>
              <h2 className={styles.reviewsTitle}>
                All Curation Comments
                {draft.recentReviews && <span className={styles.reviewsCount}>{draft.recentReviews.length}</span>}
              </h2>
            </div>

            {draft.recentReviews && draft.recentReviews.length > 0 ? (
              <div className={styles.reviewsList}>
                {draft.recentReviews.map((review) => (
                  <div key={review.id} className={styles.reviewCard}>
                    <div className={styles.reviewCardHeader}>
                      <div className={styles.reviewerInfo}>
                        <span className={styles.reviewerAvatar}>{review.reviewedBy.charAt(0).toUpperCase()}</span>
                        <span className={styles.reviewerName}>{review.reviewedBy}</span>
                      </div>
                      <div className={styles.reviewCardMeta}>
                        <span className={styles.reviewStatus} data-status={review.status}>
                          {review.status.replace('_', ' ')}
                        </span>
                        <span className={styles.reviewDate}>{getDateTimeString(new Date(review.reviewedAt))}</span>
                      </div>
                    </div>
                    <p className={styles.reviewComment}>{review.comment}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p>No curation comments yet for this draft.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default DraftReviewsPage
